// src/pages/EventsDashboard.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Chip,
  Stack,
  Divider,
  Drawer,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LaunchIcon from "@mui/icons-material/Launch";
import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useNavigate } from "react-router-dom";
import ChecklistIcon from "@mui/icons-material/Checklist";

import Sidebar from "../components/ui/Sidebar";
import PageHeader from "../components/ui/PageHeader";
import CardGridComponent from "../components/ui/CardGridComponent";
import CardComponent from "../components/ui/CardComponent";
import TableComponent from "../components/ui/TableComponent";
import axios from "axios";
import { auth, storage } from "../firebase-config";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { useSidebar } from "../context/SidebarContext";
import { useToast } from "../components/ui/ToastContext";

// --- Helpers for time handling (similar to ClientDocumentCard) ---

// Normalize Firestore Timestamp / admin timestamp / ISO / number → JS Date
const normalizeToDate = (ts) => {
  if (!ts) return null;

  // Firestore client Timestamp
  if (typeof ts?.toDate === "function") {
    try {
      return ts.toDate();
    } catch {
      // fall through
    }
  }

  // Admin timestamp-like { _seconds, _nanoseconds } or { seconds, nanoseconds }
  if (typeof ts === "object") {
    const seconds = ts._seconds ?? ts.seconds;
    if (typeof seconds === "number") {
      return new Date(seconds * 1000);
    }
  }

  // Milliseconds since epoch
  if (typeof ts === "number") {
    return new Date(ts);
  }

  // ISO string
  if (typeof ts === "string") {
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime())) return d;
  }

  return null;
};

const computeRollupsFromAttendees = (attendees = []) => {
  const totalRsvps = attendees.length;

  const totalCheckIns = attendees.filter((a) => {
    const s = String(a.checkInStatus || "").toLowerCase();
    // anything not pending counts as checked in
    return s && s !== "pending";
  }).length;

  return {
    totalRsvps,
    totalCheckIns,
    totalArrivals: totalCheckIns,
  };
};

const hydrateEventRollups = async (loadedEvents, orgId) => {
  if (!auth.currentUser || !orgId) return loadedEvents;

  const idToken = await auth.currentUser.getIdToken(true);

  // small concurrency limit so you don’t hammer Cloud Run
  const limit = 4;
  const queue = [...loadedEvents];
  const results = [];
  const workers = Array.from({ length: mentionOrZero(limit) }, async () => {
    while (queue.length) {
      const evt = queue.shift();
      try {
        const { data } = await axios.post(
          GET_EVENT_ATTENDEES_URL,
          { eventId: evt.eventId },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              "organization-id": orgId,
            },
          }
        );

        const attendees = Array.isArray(data?.attendees) ? data.attendees : [];
        const rollups = computeRollupsFromAttendees(attendees);

        results.push({
          ...evt,
          rsvpRollups: { ...(evt.rsvpRollups || {}), ...rollups },
        });
      } catch {
        results.push(evt);
      }
    }
  });

  await Promise.all(workers);
  return results;
};

// helper to keep ESLint happy if you paste directly
const mentionOrZero = (n) => (typeof n === "number" && n > 0 ? n : 0);

const formatDateTime = (val, timezone = "America/Chicago") => {
  const d = normalizeToDate(val);
  if (!d) return "TBD";

  const dateStr = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);

  const timeStr = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);

  return `${dateStr} • ${timeStr}`;
};

const formatDateOnly = (val, timezone = "America/Chicago") => {
  const d = normalizeToDate(val);
  if (!d) return null;

  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
};

// Timing status: Upcoming / Today / Past
const getTimingStatus = (evt) => {
  const d = normalizeToDate(evt.startDate || evt.startTime);
  if (!d) return { label: "Unscheduled", color: "default" };

  const now = new Date();

  const toYMD = (date) => ({
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
  });

  const eventYMD = toYMD(d);
  const nowYMD = toYMD(now);

  if (
    eventYMD.year === nowYMD.year &&
    eventYMD.month === nowYMD.month &&
    eventYMD.day === nowYMD.day
  ) {
    return { label: "Today", color: "success" };
  }

  if (d.getTime() < now.getTime()) {
    return { label: "Past", color: "default" };
  }

  return { label: "Upcoming", color: "primary" };
};

// CF endpoints for imports / payouts
const IMPORT_EVENTBRITE_ORDERS_URL =
  process.env.REACT_APP_IMPORT_EVENTBRITE_ORDERS_URL ||
  "https://importeventbriteorders-774073159618.us-central1.run.app";

const UPDATE_EVENT_PAYOUTS_URL =
  process.env.REACT_APP_UPDATE_EVENT_PAYOUTS_URL ||
  "https://updateeventpayouts-774073159618.us-central1.run.app";

const GET_EVENT_ATTENDEES_URL =
  process.env.REACT_APP_GET_EVENT_ATTENDEES_URL ||
  "https://geteventattendees-774073159618.us-central1.run.app";

const GENERATE_EVENT_DEMOGRAPHICS_URL =
  process.env.REACT_APP_GENERATE_EVENT_DEMOGRAPHICS_URL ||
  "https://generateeventdemographics-774073159618.us-central1.run.app";

// --- Ticketing + payouts summary chip row for card & drawer ---

const TicketingAndPayoutSummary = ({ evt }) => {
  if (!evt) return null;
  const t = evt.eventTicketing;
  const orders = t?.orders;
  const payouts = Array.isArray(evt.eventPayouts) ? evt.eventPayouts : [];
  const latestPayout = payouts.length ? payouts[payouts.length - 1] : null;

  if (!orders && !latestPayout) return null;

  return (
    <Box sx={{ mt: 1.5, mb: 1.5 }}>
      {orders && (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            size="small"
            label={`${orders.ticketsSold || 0} tickets • ${orders.count || 0} orders`}
          />
          <Chip
            size="small"
            label={`Gross $${(orders.grossSales || 0).toFixed(2)}`}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Net $${(orders.netSales || 0).toFixed(2)}`}
          />
          {orders.addonsRevenue > 0 && (
            <Chip
              size="small"
              variant="outlined"
              label={`Add-ons $${orders.addonsRevenue.toFixed(2)}`}
            />
          )}
        </Stack>
      )}

      {latestPayout && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
        >
          Latest payout:{" "}
          <strong>${(latestPayout.netPayoutAmount || 0).toFixed(2)}</strong>
          {latestPayout.periodEnd && (
            <>
              {" "}
              • through{" "}
              {formatDateOnly(latestPayout.periodEnd)}
            </>
          )}
        </Typography>
      )}
    </Box>
  );
};

// --- Event details drawer with inline payouts editor ---

const EventDetailsDrawer = ({ open, event, onClose, onSave }) => {
  const [localPayouts, setLocalPayouts] = useState([]);

  useEffect(() => {
    if (open && event) {
      const arr = Array.isArray(event.eventPayouts) ? event.eventPayouts : [];
      setLocalPayouts(arr.map((p) => ({ ...p })));
    }
  }, [open, event]);

  const handleChangePayoutField = (index, field, value) => {
    setLocalPayouts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddPayout = () => {
    setLocalPayouts((prev) => [
      ...prev,
      {
        label: "",
        netPayoutAmount: 0,
        payoutReportUrl: "",
        periodEnd: "",
      },
    ]);
  };

  const handleRemovePayout = (index) => {
    setLocalPayouts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveClick = () => {
    if (onSave) {
      onSave(localPayouts);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 420,
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="h6">
            {event?.eventName || "Event details"}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {event && (
          <>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {formatDateTime(
                event.startDate || event.startTime,
                event.timezone || "America/Chicago"
              )}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ticketing & payouts
            </Typography>
            <TicketingAndPayoutSummary evt={event} />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Event payouts (manual)
            </Typography>

            <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
              {localPayouts.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  No payouts recorded yet.
                </Typography>
              )}

              {localPayouts.map((payout, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">
                      Payout #{index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemovePayout(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <TextField
                    label="Label / Notes"
                    fullWidth
                    size="small"
                    margin="dense"
                    value={payout.label || ""}
                    onChange={(e) =>
                      handleChangePayoutField(index, "label", e.target.value)
                    }
                  />

                  <TextField
                    label="Net payout amount ($)"
                    fullWidth
                    size="small"
                    margin="dense"
                    type="number"
                    value={
                      payout.netPayoutAmount === "" ||
                      payout.netPayoutAmount === undefined
                        ? ""
                        : payout.netPayoutAmount
                    }
                    onChange={(e) =>
                      handleChangePayoutField(
                        index,
                        "netPayoutAmount",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                  />

                  <TextField
                    label="Payout report URL (PDF)"
                    fullWidth
                    size="small"
                    margin="dense"
                    value={payout.payoutReportUrl || ""}
                    onChange={(e) =>
                      handleChangePayoutField(
                        index,
                        "payoutReportUrl",
                        e.target.value
                      )
                    }
                  />

                  <TextField
                    label="Period end (YYYY-MM-DD)"
                    fullWidth
                    size="small"
                    margin="dense"
                    value={payout.periodEnd || ""}
                    onChange={(e) =>
                      handleChangePayoutField(
                        index,
                        "periodEnd",
                        e.target.value
                      )
                    }
                  />
                </Box>
              ))}
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachMoneyIcon />}
              onClick={handleAddPayout}
              sx={{ mt: 1, alignSelf: "flex-start" }}
            >
              Add payout
            </Button>

            <Box
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button onClick={onClose}>Cancel</Button>
              <Button variant="contained" onClick={handleSaveClick}>
                Save payouts
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

const EventsDashboard = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingEventId, setSendingEventId] = useState(null);
  const [formNamesById, setFormNamesById] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [savingPayouts, setSavingPayouts] = useState(false);

  // NEW: attendees modal state
  const [attendeeModalOpen, setAttendeeModalOpen] = useState(false);
  const [attendeeModalEvent, setAttendeeModalEvent] = useState(null);
  const [attendeeRows, setAttendeeRows] = useState([]);
  const [attendeeLoading, setAttendeeLoading] = useState(false);
  const [attendeeError, setAttendeeError] = useState(null);

  const orgId = sessionStorage.getItem("organizationId");
  const { isSidebarCollapsed } = useSidebar();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // -------- Metrics helpers --------

  const getAttendeeMetrics = (evt) => {
    const donorsAttendedCount = Array.isArray(evt.donorsAttended)
      ? evt.donorsAttended.length
      : 0;

    const rollups = evt.rsvpRollups || {};
    const checkInCount =
      rollups.totalArrivals ??
      rollups.totalCheckedIn ??
      rollups.totalCheckIns ??
      0;

    return {
      donorsAttendedCount,
      checkInCount,
      totalAttendees: donorsAttendedCount + checkInCount,
    };
  };

  const getRsvpOrTicketMetrics = (evt) => {
    const rollups = evt.rsvpRollups || {};
    const totalRsvps = rollups.totalRsvps ?? evt.totalRsvps ?? 0;

    const ticketRollups = evt.ticketRollups || {};
    const ticketsSold =
      ticketRollups.totalTicketsSold ?? evt.ticketsSold ?? 0;

    const isRsvpEvent =
      evt.primaryActionType === "rsvp" || evt.allowRsvps === true;

    return {
      isRsvpEvent,
      totalRsvps,
      ticketsSold,
    };
  };

  const getAudienceMatchLabel = (evt) => {
    const settings = evt.rsvpSettings || {};
    const requireMatch =
      settings.requireAudienceMatch ?? evt.requireAudienceMatch ?? false;

    if (!requireMatch) return { enabled: false, label: "Matching off" };

    const aud = Array.isArray(evt.targetAudience) ? evt.targetAudience : [];
    const joined = aud.join(", ") || "Audience match on";

    return {
      enabled: true,
      label: joined,
    };
  };

  const getCapacityInfo = (evt, totalRsvps, ticketsSold, isRsvpEvent) => {
    const max = evt.maxAttendees;
    if (!max || typeof max !== "number") {
      return { max: null, used: null, percent: null };
    }

    const used = isRsvpEvent ? totalRsvps : ticketsSold;
    if (typeof used !== "number" || used <= 0) {
      return { max, used: 0, percent: 0 };
    }

    const percent = Math.round((used / max) * 100);
    return { max, used, percent: Math.min(100, percent) };
  };

  // -------- Data loading / reload --------

  const reloadEvents = React.useCallback(async () => {
    try {
      if (!auth.currentUser) {
        console.warn("No current user; cannot load events.");
        setLoading(false);
        return;
      }

      const idToken = await auth.currentUser.getIdToken();

      // 1) Load events
      const eventsRes = await axios.get(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/getEvent",
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
        }
      );

      const loadedEvents = eventsRes.data?.events || [];

      // hydrate rollups so cards + table can show counts
      const hydrated = await hydrateEventRollups(loadedEvents, orgId);
      setEvents(hydrated); 

      // 2) Load form templates to map IDs → names (for "Assigns form on RSVP")
      try {
        if (!auth.currentUser) {
        console.warn("No current user; cannot load events.");
        setLoading(false);
        return;
      }

      const idToken = await auth.currentUser.getIdToken();

        const formsRes = await axios.post(
          "https://getformtemplates-774073159618.us-central1.run.app",
          {}, // <-- Empty body object (2nd argument)
          { // <-- Config object (3rd argument)
            headers: {
              Authorization: `Bearer ${idToken}`,
              "organization-id": orgId,
            },
          }
        );

        const raw = formsRes.data;

        // Be defensive about the shape of the response
        const templates = Array.isArray(raw?.forms)
          ? raw.forms
          : Array.isArray(raw?.templates)
          ? raw.templates
          : Array.isArray(raw?.data)
          ? raw.data
          : Array.isArray(raw)
          ? raw
          : [];

        const map = {};
        templates.forEach((t) => {
          if (!t) return;

          // Doc ID candidates
          const id =
            t.id ||
            t.formId ||
            t.formID ||
            t.docId ||
            t.documentId ||
            t.form?.id ||
            t.meta?.id;

          if (!id) return;

          // Title/name candidates
          const title =
            t.title ||
            t.name ||
            t.formTitle ||
            t.formName ||
            t.data?.title ||
            t.data?.name;

          map[id] = title || id;
        });

        setFormNamesById(map);
      } catch (formErr) {
        console.warn(
          "[EventsDashboard] Failed to load form templates for name mapping:",
          formErr
        );
      }
    } catch (e) {
      console.error("Events dashboard load failed:", e);
      addToast("Failed to load events.", "error");
    } finally {
      setLoading(false);
    }
  }, [orgId, addToast]);

  useEffect(() => {
    if (orgId) {
      reloadEvents();
    }
  }, [orgId, reloadEvents]);

  const handleSendInvites = async (evt) => {
    if (!evt?.eventId) return;
    if (!auth.currentUser) {
      addToast("You must be logged in to send invites.", "error");
      return;
    }
    if (sendingEventId === evt.eventId) return;

    try {
      setSendingEventId(evt.eventId);
      const idToken = await auth.currentUser.getIdToken(true);

      const res = await axios.post(
        "https://sendeventinvites-774073159618.us-central1.run.app",
        {
          eventId: evt.eventId,
          sendEmail: true,
          sendSms: true,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
        }
      );

      const counts = res.data?.counts || {};
      addToast(
        `Invites sent — Emails: ${counts.emailSent || 0}, SMS: ${
          counts.smsSent || 0
        }`,
        "success"
      );
    } catch (err) {
      console.error("sendEventInvites failed:", err?.response?.data || err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to send invites.";
      addToast(msg, "error");
    } finally {
      setSendingEventId(null);
    }
  };

  const handleOpenDetails = (evt) => {
    setSelectedEvent(evt);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  const handleSavePayouts = async (payouts) => {
    if (!selectedEvent || !auth.currentUser) {
      addToast("You must be logged in to save payouts.", "error");
      return;
    }

    try {
      setSavingPayouts(true);
      const idToken = await auth.currentUser.getIdToken(true);
      const headers = {
        Authorization: `Bearer ${idToken}`,
        "organization-id": orgId,
      };

      await axios.post(
        UPDATE_EVENT_PAYOUTS_URL,
        {
          eventId: selectedEvent.eventId,
          eventPayouts: payouts,
        },
        { headers }
      );

      addToast("Event payouts updated.", "success");
      setDetailsOpen(false);
      await reloadEvents();
    } catch (err) {
      console.error("[EventsDashboard] updateEventPayouts failed:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to update event payouts.";
      addToast(msg, "error");
    } finally {
      setSavingPayouts(false);
    }
  };

  // --- NEW: load attendees for a given event and open modal ---

  const handleViewAttendees = async (evt) => {
    if (!evt?.eventId) return;

    if (!auth.currentUser) {
      addToast("You must be logged in to view attendees.", "error");
      return;
    }

    try {
      setAttendeeModalEvent(evt);
      setAttendeeModalOpen(true);
      setAttendeeLoading(true);
      setAttendeeError(null);

      const idToken = await auth.currentUser.getIdToken(true);

      const { data } = await axios.post(
        GET_EVENT_ATTENDEES_URL,
        { eventId: evt.eventId, includeFormStatus: true },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
        }
      );

      const attendees = Array.isArray(data?.attendees)
        ? data.attendees
        : [];

      const tz = evt.timezone || "America/Chicago";

      // src/pages/EventsDashboard.jsx (in handleViewAttendees function)

      const rows = attendees.map((a, idx) => ({
        id: a.rsvpId || `attendee-${idx}`,
        name: a.fullName || a.name || "Unknown",
        email: a.email || "",
        phone: a.phone || "",
        partySize:
          a.partySize ??
          (typeof a.adults === "number" ||
          typeof a.kids === "number"
            ? (a.adults || 0) + (a.kids || 0)
            : ""),
        // Access the 'status' property inside the 'form' object
        formStatus:
          a.form?.status,
        source: a.source
          ? a.source.charAt(0).toUpperCase() + a.source.slice(1)
          : "Unknown",
        rsvpMethod:
          a.rsvpMethod ||
          (data?.isRsvpEvent ? "rsvp" : "ticket"),
        createdAtDisplay: a.createdAtDisplay,
      }));

      setAttendeeRows(rows);
    } catch (err) {
      console.error("[EventsDashboard] getEventAttendees failed:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        "Failed to load attendees.";
      setAttendeeError(msg);
      addToast(msg, "error");
    } finally {
      setAttendeeLoading(false);
    }
  };

  // -------- Table config (uses formatted dates) --------

  const columns = [
    { id: "eventName", label: "Event" },
    { id: "startDateDisplay", label: "Date" },
    { id: "rsvpOrTickets", label: "RSVPs / Tickets" },
    { id: "attendeeCount", label: "Attendees" },
    { id: "capacityDisplay", label: "Capacity" },
    { id: "allowRsvps", label: "RSVP Enabled" },
    { id: "visibleToPublic", label: "Published" },
  ];

  const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "—");

  const tableData = (events || []).map((evt) => {
    const tz = evt.timezone || "America/Chicago";
    const { totalAttendees } = getAttendeeMetrics(evt);
    const { isRsvpEvent, totalRsvps, ticketsSold } = getRsvpOrTicketMetrics(evt);
    const capacityInfo = getCapacityInfo(evt, totalRsvps, ticketsSold, isRsvpEvent);

    const capacityDisplay =
      capacityInfo.max != null
        ? `${capacityInfo.used ?? 0}/${capacityInfo.max} ${
            capacityInfo.percent != null ? `(${capacityInfo.percent}%)` : ""
          }`
        : "—";

    return {
      ...evt,
      startDateDisplay: formatDateTime(evt.startDate || evt.startTime, tz),
      rsvpOrTickets: isRsvpEvent ? (totalRsvps ?? 0) : (ticketsSold ?? 0),
      attendeeCount: totalAttendees ?? 0,
      capacityDisplay,
      allowRsvps: yesNo(evt.allowRsvps === true || evt.primaryActionType === "rsvp"),
      visibleToPublic: yesNo(evt.visibleToPublic === true),
    };
  });

  // -------- Card-level component with MoreVert menu (styled like ClientDocumentCard) --------

  const EventCard = ({ evt }) => {
    const [actionsAnchor, setActionsAnchor] = useState(null);
    const [importingCsv, setImportingCsv] = useState(false);
    const [isGeneratingDemographics, setIsGeneratingDemographics] = useState(false);
    const fileInputRef = React.useRef(null);

    const tz = evt.timezone || "America/Chicago";
    const { totalAttendees, donorsAttendedCount, checkInCount } =
      getAttendeeMetrics(evt);
    const { isRsvpEvent, totalRsvps, ticketsSold } =
      getRsvpOrTicketMetrics(evt);
    const audienceMatch = getAudienceMatchLabel(evt);
    const timingStatus = getTimingStatus(evt);
    const capacityInfo = getCapacityInfo(
      evt,
      totalRsvps,
      ticketsSold,
      isRsvpEvent
    );

const handleGenerateDemographics = async () => {
  handleCloseActions();

  if (!auth.currentUser) {
    addToast("You must be logged in to generate demographics.", "error");
    return;
  }

  try {
    setIsGeneratingDemographics(true);

    const idToken = await auth.currentUser.getIdToken(true);
    const headers = {
      Authorization: `Bearer ${idToken}`,
      "organization-id": orgId,
    };

    // Currently hard-coded to the Holiday Toy Giveaway form
    const formId = "form_zw02cdzj";

    const { data } = await axios.post(
      GENERATE_EVENT_DEMOGRAPHICS_URL,
      {
        eventId: evt.eventId,
        formId,
        dryRun: false, // real run: generate PDF (backfill CF handles client updates)
      },
      { headers }
    );

    const families = data?.familiesIncluded ?? data?.familiesCount ?? 0;
    const totalChildren = data?.totalChildren ?? 0;

    addToast(
      `Demographics sheet generated for ${families} families and ${totalChildren} children.`,
      "success"
    );

    // CF returns: pdf: { path, url }
    const pdfUrl = data?.pdf?.url || data?.publicUrl || null;
    if (pdfUrl) {
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
    }
  } catch (err) {
    console.error("[EventsDashboard] generateEventDemographics error:", err);
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      "Failed to generate event demographics.";
    addToast(msg, "error");
  } finally {
    setIsGeneratingDemographics(false);
  }
};

    const formattedDateTime = formatDateTime(
      evt.startDate || evt.startTime,
      tz
    );
    const registrationDeadlineLabel = formatDateOnly(
      evt.registrationDeadline,
      tz
    );

    const imageUrl =
      evt.eventImage || evt.micrositeConfig?.backgroundImageUrl || null;

    const micrositeUrl =
      evt.micrositeConfig?.publicUrl ||
      (evt.visibleToPublic
        ? `https://app.kinovacrm.com/${orgId}/${evt.eventId}`
        : null);

    const previewUrl =
      evt.micrositeConfig?.previewUrl ||
      `https://preview.kinovacrm.com/${orgId}/${evt.eventId}`;

    const isSending = sendingEventId === evt.eventId;

    // Form name mapping (Assigns form on RSVP)
    const formId =
      evt.rsvpSettings?.assignFormIdOnRsvp ||
      evt.assignFormIdOnRsvp ||
      null;
    const formName = formId ? formNamesById[formId] || formId : null;

    const handleOpenActions = (e) => setActionsAnchor(e.currentTarget);
    const handleCloseActions = () => setActionsAnchor(null);

    const handleOpenMicrosite = () => {
      if (!micrositeUrl) return;
      window.open(micrositeUrl, "_blank", "noopener,noreferrer");
    };

    const handleOpenPreview = () => {
      if (!previewUrl) return;
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    };

    const handleUploadCsvClick = () => {
      handleCloseActions();
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleCsvSelected = async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;

      if (!auth.currentUser) {
        addToast(
          "You must be logged in to upload Eventbrite CSV.",
          "error"
        );
        return;
      }

      try {
        setImportingCsv(true);
        const path = `organizations/${orgId}/events/${evt.eventId}/imports/eventbrite/${file.name}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file);

        const idToken = await auth.currentUser.getIdToken(true);
        const headers = {
          Authorization: `Bearer ${idToken}`,
          "organization-id": orgId,
        };

        const { data } = await axios.post(
          IMPORT_EVENTBRITE_ORDERS_URL,
          {
            eventId: evt.eventId,
            storagePath: path,
          },
          { headers }
        );

        const summary = data?.summary || {};
        addToast(
          `Imported Eventbrite orders: ${
            summary.ticketsSold || 0
          } tickets, $${(summary.grossSales || 0).toFixed(2)} gross.`,
          "success"
        );

        await reloadEvents();
      } catch (err) {
        console.error(
          "[EventsDashboard] importEventbriteOrders error:",
          err
        );
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to import Eventbrite orders.";
        addToast(msg, "error");
      } finally {
        setImportingCsv(false);
      }
    };

    return (
      <CardComponent
        id={evt.eventId}
        title={evt.eventName || "Untitled Event"}
        size="medium"
        shape="rectangle"
        isDraggable={false}
      >
        {/* Hidden file input for Eventbrite CSV upload */}
        <input
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleCsvSelected}
        />

        {/* Header: title, date, timing + overflow menu */}
        <Box
          className="card-header"
          sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {evt.eventName || "Untitled Event"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {formattedDateTime}
            </Typography>
            {evt.location && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.25 }}
              >
                {evt.location}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 0.5,
            }}
          >
            <Stack direction="row" spacing={0.5}>
              <Chip
                label={evt.eventStatus || "Scheduled"}
                size="small"
                variant="outlined"
              />
              <Chip
                label={timingStatus.label}
                size="small"
                color={timingStatus.color}
                variant="outlined"
              />
            </Stack>

            {/* Actions menu icon */}
            <IconButton size="small" onClick={handleOpenActions}>
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Banner image */}
        {imageUrl && (
          <Box
            mt={0.5}
            mb={1.5}
            sx={{
              borderRadius: 1,
              overflow: "hidden",
              maxHeight: 180,
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <img
              src={imageUrl}
              alt={evt.eventName || "Event banner"}
              style={{
                width: "100%",
                display: "block",
                objectFit: "cover",
              }}
            />
          </Box>
        )}

        {/* Metrics row */}
        <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
          {/* RSVPs / Tickets metric now clickable to open attendees modal */}
          <Box
            sx={{ cursor: "pointer" }}
            onClick={() => handleViewAttendees(evt)}
          >
            <Typography variant="caption" color="text.secondary">
              {isRsvpEvent ? "RSVPs" : "Tickets Sold"}
            </Typography>
            <Typography variant="body1">
              {isRsvpEvent ? totalRsvps : ticketsSold}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{ textDecoration: "underline" }}
            >
              View attendees
            </Typography>
          </Box>

          <Box
            sx={{ cursor: "pointer" }}
            onClick={() =>
              navigate(`/checkin/status?org=${orgId}&event=${evt.eventId}`)
            }
          >
            <Typography variant="caption" color="text.secondary">
              Attendees (checked in)
            </Typography>

            <Typography variant="body1">
              {totalAttendees}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              {`Donors: ${donorsAttendedCount}, Check-ins: ${checkInCount}`}
            </Typography>

            <Typography
              variant="caption"
              color="primary"
              sx={{
                textDecoration: "underline",
                mt: 0.5,          // 👈 spacing added here
                display: "block", // ensures margin applies properly
              }}
            >
              View check-in status
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Budget vs Revenue
            </Typography>
            <Typography variant="body1">
              ${evt.budget || 0} / ${evt.revenue || 0}
            </Typography>
          </Box>
        </Stack>

        {/* Ticketing + payouts summary */}
        <TicketingAndPayoutSummary evt={evt} />

        {/* Capacity + registration */}
        <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Capacity
            </Typography>
            <Typography variant="body1">
              {capacityInfo.max != null
                ? `${capacityInfo.used ?? 0}/${capacityInfo.max}${
                    capacityInfo.percent != null
                      ? ` (${capacityInfo.percent}%)`
                      : ""
                  }`
                : "Not set"}
            </Typography>
          </Box>

          {registrationDeadlineLabel && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Registration deadline
              </Typography>
              <Typography variant="body1">
                {registrationDeadlineLabel}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Chips: microsite, RSVP, audience */}
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          {micrositeUrl && (
            <Chip
              size="small"
              label="Microsite Live"
              color="success"
              variant="outlined"
              icon={<VisibilityIcon fontSize="small" />}
            />
          )}
          {!micrositeUrl && (
            <Chip
              size="small"
              label="Microsite Draft"
              variant="outlined"
              icon={<LaunchIcon fontSize="small" />}
            />
          )}
          {evt.allowRsvps && (
            <Chip
              size="small"
              label="RSVP Enabled"
              color="info"
              variant="outlined"
              icon={<PeopleIcon fontSize="small" />}
            />
          )}
          {audienceMatch.enabled && (
            <Chip
              size="small"
              label={`Matching: ${audienceMatch.label}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Form assignment */}
        {formName && (
          <Typography variant="caption" color="text.secondary">
            Assigns form on RSVP: <strong>{formName}</strong>
          </Typography>
        )}

        {/* Actions menu */}
        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={handleCloseActions}
        >
          {/* View details / payouts */}
          <MenuItem
            onClick={() => {
              handleCloseActions();
              handleOpenDetails(evt);
            }}
          >
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">View details / payouts</Typography>
          </MenuItem>

          <Divider />

          {/* View live microsite */}
          <MenuItem
            onClick={() => {
              handleCloseActions();
              handleOpenMicrosite();
            }}
            disabled={!micrositeUrl}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">View microsite</Typography>
          </MenuItem>

          {/* View preview microsite */}
          <MenuItem
            onClick={() => {
              handleCloseActions();
              handleOpenPreview();
            }}
          >
            <ListItemIcon>
              <LaunchIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">View preview</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleCloseActions();
              navigate(`/checkin/status?org=${orgId}&event=${evt.eventId}`);
            }}
          >
            <ListItemIcon>
              <ChecklistIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">Check-in status</Typography>
          </MenuItem>

          {/* Upload Eventbrite CSV + import */}
          <MenuItem
            onClick={handleUploadCsvClick}
            disabled={importingCsv}
          >
            <ListItemIcon>
              <CloudUploadIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">
              {importingCsv ? "Importing…" : "Upload External Sales CSV"}
            </Typography>
          </MenuItem>

          <Divider />

          {/* NEW: View attendees */}
          <MenuItem
            onClick={() => {
              handleCloseActions();
              handleViewAttendees(evt);
            }}
          >
            <ListItemIcon>
              <PeopleIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">View attendees…</Typography>
          </MenuItem>

          {/* Send invites */}
          <MenuItem
            onClick={async () => {
              handleCloseActions();
              await handleSendInvites(evt);
            }}
            disabled={isSending}
          >
            <ListItemIcon>
              <SendIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">
              {isSending ? "Sending invites…" : "Send invites…"}
            </Typography>
          </MenuItem>

          {/* Generate demographics sheet */}
        <MenuItem
          onClick={handleGenerateDemographics}
          disabled={isGeneratingDemographics}
        >
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2">
            {isGeneratingDemographics
              ? "Generating demographics…"
              : "Download demographics sheet"}
          </Typography>
        </MenuItem>
        </Menu>
      </CardComponent>
    );
  };

  // -------- Cards for grid --------

  const eventCards = (events || []).slice(0, 6).map((evt, index) => ({
    id: evt.eventId || `event-${index}`,
    size: "medium",
    shape: "rectangle",
    isDraggable: false,
    content: <EventCard evt={evt} />,
  }));

  return (
    <div className="manage-users-container">
      <Sidebar />
      <div
        className={`manage-users-content ${
          isSidebarCollapsed ? "expanded" : ""
        }`}
      >
        <Box sx={{ width: "100%" }}>
          <PageHeader title="Events Dashboard" />

          {/* Details drawer (payout editor) */}
          <EventDetailsDrawer
            open={detailsOpen}
            event={selectedEvent}
            onClose={handleCloseDetails}
            onSave={handleSavePayouts}
          />

          {/* Attendees modal */}
          <Dialog
            open={attendeeModalOpen}
            onClose={() => setAttendeeModalOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle>
              {attendeeModalEvent?.eventName || "Event"} – Attendees
            </DialogTitle>
            <DialogContent dividers>
              {attendeeLoading ? (
                <Typography sx={{ py: 2 }}>Loading attendees…</Typography>
              ) : attendeeError ? (
                <Alert severity="error" sx={{ my: 1 }}>
                  {attendeeError}
                </Alert>
              ) : attendeeRows.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No attendees found yet.
                </Typography>
              ) : (
                <TableComponent
                  columns={[
                    { id: "name", label: "Name" },
                    { id: "email", label: "Email" },
                    { id: "phone", label: "Phone" },
                    { id: "partySize", label: "Party Size" },
                    { id: "formStatus", label: "Form Status" },
                    { id: "source", label: "Source" },
                    { id: "createdAtDisplay", label: "RSVP Date",  sortType: "date", },
                  ]}
                  data={attendeeRows}
                  sortable
                  paginated
                  selectable={false}
                  persistKey="eventAttendeesModal"
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAttendeeModalOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {loading ? (
            <Typography sx={{ p: 3 }}>Loading…</Typography>
          ) : (
            <>
              {/* Top event cards with vertical breathing room */}
              <Box mt={2} mb={3}>
                <CardGridComponent
                  cards={eventCards}
                  isSidebarCollapsed={isSidebarCollapsed}
                />
              </Box>

              {/* Full events table with bottom margin so it doesn't hug footer */}
              <Box mt={1} mb={4}>
                <CardComponent title="All Events">
                  <TableComponent
                    columns={columns}
                    data={tableData}
                    sortable
                    paginated
                    selectable={false}
                    persistKey="eventsDashboard"
                  />
                </CardComponent>
              </Box>
            </>
          )}
        </Box>
      </div>
    </div>
  );
};

export default EventsDashboard;